import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { CustomerApiKeyItem } from "../../../stores/CustomerStore";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

interface CustomerApiKeyListProps {
  customerId: number;
  refreshTrigger?: number;
}

const headerCellSx = (colors: ReturnType<typeof getModernPageColors>) => ({
  color: colors.label,
  fontSize: "0.75rem",
  fontWeight: 600,
  py: 1.5,
  borderBottom: `1px solid ${colors.border}`,
  backgroundColor: colors.headerBg,
});

const bodyCellSx = (colors: ReturnType<typeof getModernPageColors>) => ({
  fontSize: "0.8rem",
  color: colors.title,
  py: 2,
  borderBottom: `1px solid ${colors.border}`,
});

interface ApiKeyListUiState {
  loadError: string | null;
  isConfirmOpen: boolean;
  isDisabling: boolean;
}

const CustomerApiKeyList: React.FC<CustomerApiKeyListProps> = ({
  customerId,
  refreshTrigger,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const c = theme.palette.customColors;
  const { customerStore } = useStores();
  const { showError } = useNotification();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedKey, setSelectedKey] = useState<CustomerApiKeyItem | null>(
    null,
  );
  const [uiState, setUiState] = useState<ApiKeyListUiState>({
    loadError: null,
    isConfirmOpen: false,
    isDisabling: false,
  });
  const { loadError, isConfirmOpen, isDisabling } = uiState;

  const updateUiState = (patch: Partial<ApiKeyListUiState>) =>
    setUiState((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    if (!customerId) return;

    updateUiState({ loadError: null });
    customerStore.fetchPartnerApiKeys(customerId).catch((err: any) => {
      updateUiState({
        loadError:
          err?.response?.data?.message || "Failed to load partner API keys.",
      });
    });
  }, [customerId, refreshTrigger, customerStore]);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    keyItem: CustomerApiKeyItem,
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedKey(keyItem);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleOpenDisableConfirm = () => {
    handleCloseMenu();
    updateUiState({ isConfirmOpen: true });
  };

  const handleConfirmDisable = async () => {
    if (!selectedKey || !customerId) return;
    updateUiState({ isDisabling: true });
    try {
      await customerStore.disablePartnerApiKey(customerId, selectedKey.id);
      updateUiState({ isConfirmOpen: false });
      setSelectedKey(null);
    } catch (err: any) {
      console.error("Failed to disable API key:", err);
      showError(
        "Error",
        err?.response?.data?.message ||
          err?.message ||
          "Failed to disable API key.",
      );
    } finally {
      updateUiState({ isDisabling: false });
    }
  };

  // API returns keys newest first.
  const keys = customerStore.partnerApiKeys || [];

  const headSx = headerCellSx(colors);
  const cellSx = bodyCellSx(colors);

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: colors.surface,
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        boxShadow: `0 1px 3px ${c.shadowVeryLight}`,
        p: { xs: 2.5, sm: 3.5 },
        mb: 3,
      }}
    >
      <CustomTypography
        variant="heading5"
        color={colors.title}
        style={{ lineHeight: 1.3 }}
      >
        Your Keys
      </CustomTypography>
      <CustomTypography
        variant="body2"
        color={colors.subtitle}
        style={{ marginTop: 2, marginBottom: 20 }}
      >
        Manage your API keys.
      </CustomTypography>

      <TableContainer
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          backgroundColor: colors.surface,
          overflow: "hidden",
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="partner api keys table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, pl: 2.5, width: 70 }}>S.no</TableCell>
              <TableCell sx={headSx}>Secret Key</TableCell>
              <TableCell sx={headSx}>API key</TableCell>
              <TableCell sx={headSx}>Created On</TableCell>
              <TableCell sx={headSx}>Status</TableCell>
              <TableCell align="right" sx={{ ...headSx, pr: 2.5, width: 90 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {customerStore.isPartnerApiKeysLoading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 5 }}>
                  <CircularProgress
                    size={28}
                    sx={{ color: colors.accentPurple }}
                  />
                </TableCell>
              </TableRow>
            ) : loadError ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: colors.error,
                    fontSize: "0.8rem",
                  }}
                >
                  {loadError}
                </TableCell>
              </TableRow>
            ) : keys.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: colors.subtitle,
                    fontSize: "0.8rem",
                  }}
                >
                  No API keys generated yet. Click &quot;Generate API Key&quot;
                  above to create one.
                </TableCell>
              </TableRow>
            ) : (
              keys.map((row, index) => {
                const isActive = row.is_active;
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: colors.surfaceSoft },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ ...cellSx, pl: 2.5, fontWeight: 600 }}>
                      {index + 1}.
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        fontFamily: "monospace",
                        color: c.navyText,
                      }}
                    >
                      {row.masked_secret_key || "••••••••••••"}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellSx,
                        fontFamily: "monospace",
                        color: c.navyText,
                      }}
                    >
                      {row.masked_api_key || `${row.key_prefix}...`}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: c.navyText }}>
                      {formatDate(row.created_at)}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <CustomTypography
                        variant="body2"
                        color={isActive ? colors.success : c.mediumGray}
                        fontWeight={500}
                      >
                        {isActive ? "Active" : "Disabled"}
                      </CustomTypography>
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellSx, pr: 2.5 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, row)}
                        sx={{
                          color: c.mutedSlateText,
                          "&:hover": {
                            backgroundColor: c.lightGreyColor,
                          },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: `0 4px 20px ${c.shadowLightBorder}`,
            border: `1px solid ${colors.border}`,
            minWidth: 130,
            py: 0.5,
            backgroundColor: colors.surface,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={handleOpenDisableConfirm}
          disabled={!selectedKey?.is_active}
          sx={{
            fontSize: "0.8rem",
            py: 1,
            color: selectedKey?.is_active ? c.navyText : c.mediumGray,
            borderRadius: 1,
            mx: 0.5,
            "&:hover": {
              backgroundColor: colors.headerBg,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 26, color: "inherit" }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: "0.8rem", fontWeight: 500 }}
          >
            Disable
          </ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={isConfirmOpen}
        onClose={() => !isDisabling && updateUiState({ isConfirmOpen: false })}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1.5,
            maxWidth: 440,
            backgroundColor: colors.surface,
          },
        }}
      >
        <DialogTitle sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <CustomTypography variant="heading4" color={colors.title}>
            Disable Partner API Key?
          </CustomTypography>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 1.5 }}>
          <CustomTypography variant="body1" color={colors.subtitle}>
            Are you sure you want to disable API key{" "}
            <Box component="span" sx={{ fontWeight: 700, color: colors.title }}>
              {selectedKey?.key_prefix}...
            </Box>
            ? External integrations and systems using this key will immediately
            lose access. This action cannot be undone.
          </CustomTypography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <AppButton
            label="Cancel"
            variant="grayscale"
            disabled={isDisabling}
            onClick={() => updateUiState({ isConfirmOpen: false })}
            customStyles={{ minWidth: 96 }}
          />
          <AppButton
            label={
              isDisabling ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Disable Key"
              )
            }
            variant="cancel"
            disabled={isDisabling}
            onClick={() => {
              void handleConfirmDisable();
            }}
            customStyles={{ minWidth: 120 }}
          />
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default observer(CustomerApiKeyList);
